// ============================================================================
// Product metrics — length of stay, site popularity, site-type trends,
// post-booking site additions, and estimated overage-fee revenue.
// ============================================================================

import { StoreOverageFee } from '../../types/site';
import { bucketKey, bucketLabel, enumerateBuckets, groupBy, median, sumBy } from './aggregations';
import { AnalysisStay, Granularity, isRevenueStay } from './types';

// ============================================================================
// Length of stay
// ============================================================================

export interface LosBucket {
  label: string;
  count: number;
}

const LOS_BUCKETS: Array<{ label: string; min: number; max: number }> = [
  { label: '1 night', min: 1, max: 1 },
  { label: '2 nights', min: 2, max: 2 },
  { label: '3–4', min: 3, max: 4 },
  { label: '5–7', min: 5, max: 7 },
  { label: '8–14', min: 8, max: 14 },
  { label: '15+', min: 15, max: Infinity },
];

export function losDistribution(stays: AnalysisStay[], from: string, to: string): LosBucket[] {
  const buckets = LOS_BUCKETS.map(b => ({ label: b.label, count: 0 }));
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const idx = LOS_BUCKETS.findIndex(b => stay.days >= b.min && stay.days <= b.max);
    if (idx >= 0) buckets[idx].count += 1;
  }
  return buckets;
}

export function medianLos(stays: AnalysisStay[], from: string, to: string): number | null {
  const days = stays
    .filter(isRevenueStay)
    .filter(s => s.checkIn >= from && s.checkIn <= to)
    .map(s => s.days);
  if (days.length === 0) return null;
  return median(days);
}

// ============================================================================
// Site popularity
// ============================================================================

export interface SitePopularityRow {
  siteId: number;
  siteAlias: string;
  bookings: number;
  nights: number;
  revenue: number;
}

export function sitePopularity(stays: AnalysisStay[], from: string, to: string): SitePopularityRow[] {
  const rows = new Map<number, SitePopularityRow>();
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    for (const site of stay.sites) {
      if (site.status !== 'active') continue;
      let row = rows.get(site.siteId);
      if (!row) {
        row = { siteId: site.siteId, siteAlias: site.siteAlias, bookings: 0, nights: 0, revenue: 0 };
        rows.set(site.siteId, row);
      }
      row.bookings += 1;
      row.nights += stay.days;
      row.revenue += site.siteCost;
    }
  }
  return Array.from(rows.values()).sort((a, b) => b.revenue - a.revenue);
}

// ============================================================================
// Site-type revenue trend (multi-series)
// ============================================================================

export interface SiteTypeTrend {
  /** Series names in fixed order (stable sorted type id; >6 folded into "Other"). */
  series: string[];
  /** One row per bucket: { bucket, label, [typeName]: revenue } */
  data: Array<Record<string, string | number>>;
}

const MAX_TYPE_SERIES = 6;

export function siteTypeTrend(
  stays: AnalysisStay[],
  from: string,
  to: string,
  granularity: Granularity,
  typeIdBySiteId: Map<number, number>,
  typeNameById: Map<number, string>
): SiteTypeTrend {
  // Fixed series order by stable sorted type id — a type keeps its color
  // even when a range change removes other types.
  const sortedTypeIds = Array.from(typeNameById.keys()).sort((a, b) => a - b);
  const keptIds = sortedTypeIds.slice(0, MAX_TYPE_SERIES);
  const foldOthers = sortedTypeIds.length > MAX_TYPE_SERIES;
  const series = [
    ...keptIds.map(id => typeNameById.get(id)!),
    ...(foldOthers ? ['Other'] : []),
  ];

  const buckets = enumerateBuckets(from, to, granularity);
  const data = buckets.map(key => {
    const row: Record<string, string | number> = { bucket: key, label: bucketLabel(key, granularity) };
    for (const name of series) row[name] = 0;
    return row;
  });
  const rowByBucket = new Map(data.map(row => [row.bucket as string, row]));

  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const row = rowByBucket.get(bucketKey(stay.checkIn, granularity));
    if (!row) continue;
    for (const site of stay.sites) {
      if (site.status !== 'active') continue;
      const typeId = typeIdBySiteId.get(site.siteId);
      const name =
        typeId !== undefined && keptIds.includes(typeId)
          ? typeNameById.get(typeId)!
          : foldOthers || typeId === undefined
            ? 'Other'
            : typeNameById.get(typeId)!;
      if (!(name in row)) row[name] = 0;
      row[name] = (row[name] as number) + site.siteCost;
    }
  }

  // "Other" may have been introduced by unmapped sites even without folding
  const hasOther = data.some(row => (row['Other'] as number) > 0);
  const finalSeries = hasOther && !series.includes('Other') ? [...series, 'Other'] : series;
  return { series: finalSeries, data };
}

// ============================================================================
// Post-booking site additions
// ============================================================================

export function siteAddRate(stays: AnalysisStay[], from: string, to: string): number | null {
  const rows = stays
    .filter(isRevenueStay)
    .filter(s => s.checkIn >= from && s.checkIn <= to);
  if (rows.length === 0) return null;
  const withAdds = rows.filter(s => s.sites.some(site => site.addedAfterBooking)).length;
  return Math.round((withAdds / rows.length) * 1000) / 10;
}

// ============================================================================
// Estimated overage-fee revenue
// ============================================================================

export interface FeeTrendPoint {
  bucket: string;
  label: string;
  adult: number;
  child: number;
  pet: number;
  vehicle: number;
}

const PARTY_FIELD: Record<string, (s: AnalysisStay) => number> = {
  adult: s => s.adults,
  child: s => s.children,
  pet: s => s.pets,
  vehicle: s => s.vehicles,
};

/**
 * ESTIMATE: reservations don't itemize fee lines, so this reconstructs
 * overage fees from the configured store overage fees × party counts:
 * overage = max(0, count − base_included), fee = amount × (flat ? 1 : overage),
 * × nights when frequency is 'nightly'. Exact numbers need a reservation
 * cost-breakdown endpoint (design doc §6.3).
 */
export function feeTrendEstimate(
  stays: AnalysisStay[],
  overageFees: StoreOverageFee[],
  from: string,
  to: string,
  granularity: Granularity
): FeeTrendPoint[] {
  const activeFees = overageFees.filter(f => f.active);
  const feesByType = groupBy(activeFees, f => f.fee_type);
  const buckets = enumerateBuckets(from, to, granularity);
  const points = buckets.map(key => ({
    bucket: key,
    label: bucketLabel(key, granularity),
    adult: 0,
    child: 0,
    pet: 0,
    vehicle: 0,
  }));
  const pointByBucket = new Map(points.map(p => [p.bucket, p]));

  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const point = pointByBucket.get(bucketKey(stay.checkIn, granularity));
    if (!point) continue;
    feesByType.forEach((fees, feeType) => {
      const getCount = PARTY_FIELD[feeType];
      if (!getCount) return;
      for (const fee of fees) {
        const overage = Math.max(0, Math.min(getCount(stay), fee.max_allowed) - fee.base_included);
        if (overage === 0) continue;
        const units = fee.flat ? 1 : overage;
        const nights = fee.frequency === 'nightly' ? stay.days : 1;
        point[feeType as keyof Pick<FeeTrendPoint, 'adult' | 'child' | 'pet' | 'vehicle'>] +=
          fee.amount * units * nights;
      }
    });
  }

  for (const point of points) {
    point.adult = Math.round(point.adult * 100) / 100;
    point.child = Math.round(point.child * 100) / 100;
    point.pet = Math.round(point.pet * 100) / 100;
    point.vehicle = Math.round(point.vehicle * 100) / 100;
  }
  return points;
}

export function feeEstimateTotal(points: FeeTrendPoint[]): number {
  return sumBy(points, p => p.adult + p.child + p.pet + p.vehicle);
}
